import type { CheckListItem } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector/CheckList';
import type { IconButtonCheckListItem } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/FilterSelector/IconButtonCheckList';
import { TestLabels } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import type { ProductsRouter } from 'src/modules/designStudio/helpers/productSelection/ProductsRouter';
import {
  ColorGroupDisplayHex,
  ColorGroupNames,
} from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import { ProductTypeNames } from 'src/modules/designStudio/helpers/productSelection/constants';
import type {
  ProductFilter,
  ProductFilterOptions,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { ProductTypeLabel } from 'src/modules/designStudio/helpers/productSelection/types';

import ShingledRoofImg from 'assets/img/ds-menu/asphaltRoof.png';
import BrickImg from 'assets/img/ds-menu/brick.png';
import ConcreteImg from 'assets/img/ds-menu/concrete.png';
import HorizontalImg from 'assets/img/ds-menu/horizontal.png';
import TileRoofImg from 'assets/img/ds-menu/metalRoof.png';
import ShingledImg from 'assets/img/ds-menu/shingles.png';
import StoneImg from 'assets/img/ds-menu/stone.png';
import StuccoImg from 'assets/img/ds-menu/stucco.png';
import MetalRoofImg from 'assets/img/ds-menu/tileRoof.png';
import VerticalImg from 'assets/img/ds-menu/vertical.png';

const ProductTypeImg: Record<ProductTypeLabel, string> = {
  [ProductTypeLabel.HORIZONTAL_SIDING]: HorizontalImg,
  [ProductTypeLabel.VERTICAL_SIDING]: VerticalImg,
  [ProductTypeLabel.SHINGLED_SIDING]: ShingledImg,
  [ProductTypeLabel.BRICK_SIDING]: BrickImg,
  [ProductTypeLabel.STONE_SIDING]: StoneImg,
  [ProductTypeLabel.CONCRETE_SIDING]: ConcreteImg,
  [ProductTypeLabel.STUCCO_SIDING]: StuccoImg,

  [ProductTypeLabel.SHINGLE_ROOF]: ShingledRoofImg,
  [ProductTypeLabel.TILE_ROOF]: TileRoofImg,
  [ProductTypeLabel.METAL_ROOF]: MetalRoofImg,

  [ProductTypeLabel.TRIM]: HorizontalImg,
};

export const createProductTypeCheckListItems = (
  options: ProductFilterOptions,
  selection: ProductFilter,
): IconButtonCheckListItem[] =>
  options.productTypes.map(type => ({
    id: `${type}`,
    name: ProductTypeNames[type],
    isChecked: type === selection.productType,
    imgSrc: ProductTypeImg[type],
    label: TestLabels.FILTER_PRODUCT_OPTION,
    dataTestId: `${type}`,
  }));

export const createColorCheckListItems = (
  options: ProductFilterOptions,
  selection: ProductFilter,
): IconButtonCheckListItem[] =>
  options.colorGroups.map(group => ({
    id: group,
    name: ColorGroupNames[group],
    isChecked: selection.colorGroups.some(g => g === group),
    color: ColorGroupDisplayHex[group],
    label: TestLabels.FILTER_COLOR_OPTION,
    dataTestId: `${ColorGroupNames[group]}`,
  }));

export const createBrandCheckListItems = (
  options: ProductFilterOptions,
  selection: ProductFilter,
  dataStore?: ProductsRouter,
): CheckListItem[] => {
  if (!dataStore) {
    return [];
  }

  return options.providers
    .map(provider => ({
      id: provider,
      name: dataStore.getProviderNameByUUID(provider) as string,
      isChecked: selection.providers.some(s => s === provider),
      label: TestLabels.FILTER_BRAND_OPTION,
      dataTestId: provider,
    }))
    .filter(item => item.name !== undefined);
};

export const createSimpleCheckListItems = (
  options: string[],
  selection: string[],
  label: string,
): CheckListItem[] =>
  options.map(option => ({
    id: option,
    name: option,
    isChecked: selection.some(s => s === option),
    label,
    dataTestId: option,
  }));

export const treatCheckListSelectionAsRadioSelection = (
  ids: string[],
  currentSelection: string,
): string => {
  if (ids.length === 0) {
    return currentSelection;
  }
  if (ids.length > 1) {
    return ids.find(
      id => parseInt(id, 10) !== parseInt(currentSelection, 10),
    ) as string;
  }
  return currentSelection;
};

export const treatCheckListSelectionAsRadioSelectionForColors = (
  ids: string[],
  currentSelection: string | undefined,
): string[] => {
  if (currentSelection === undefined) {
    return [ids[0]];
  }
  const value = ids.find(id => id !== currentSelection);
  return value === undefined ? [] : [value];
};

export const formatFilterOptions = (
  filterOptions: ProductFilterOptions,
): ProductFilterOptions => {
  filterOptions.styles.sort();
  filterOptions.textures.sort();
  filterOptions.sizes.sort();
  return filterOptions;
};
